import { useEffect, useMemo, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ApprovalNoticeBox from '@/components/Approval/@components/ApprovalNoticeBox';
import {
  buildInfluencerMonthlyReportContentHtml,
  getInfluencerMonthlySubject,
  InfluencerMonthlyReportSnapshot,
} from '@/components/Approval/@components/InfluencerMonthlyReport';
import { getInfluencerMonthlyReportDraftSourceUrl } from '@/constant/approval';
import { useSnackbar } from '@/hooks/global-tools';
import { getNestedDataFetcher } from '@/utils/apis';
import {
  createInfluencerMonthlyReportState,
  INFLUENCER_MONTHLY_LOAD_FAILED_MESSAGE,
  INFLUENCER_MONTHLY_MISSING_MESSAGE,
  pickInfluencerMonthlyReport,
  type InfluencerMonthlyDraftSource,
} from '@/utils/approval/influencerMonthlyReport';
import { FieldBlock } from './styles';
import type { ApprovalFormValues } from './types';

type InfluencerMonthlyReportFieldProps = {
  /** 신규는 양식 응답, 편집은 저장값 복원이 끝난 뒤 true — 그때 일련번호로 1회 조회 */
  ready: boolean;
};

/**
 * 인플루언서 월마감 보고서(108) — 일련번호로 월마감 리포트를 조회해 표로.
 * 편집으로 다시 열어도 다시 조회하고 제목도 덮어씀(레거시 동일), 재상신은 다시 조회한 표로 저장
 */
const InfluencerMonthlyReportField = ({
  ready,
}: InfluencerMonthlyReportFieldProps) => {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const { control, getValues, setValue } = useFormContext<ApprovalFormValues>();
  const state = useWatch({ control, name: 'influencerMonthlyReport' });
  const startedRef = useRef(false);
  const aliveRef = useRef(true);

  useEffect(
    () => () => {
      aliveRef.current = false;
    },
    [],
  );

  useEffect(() => {
    if (!ready || startedRef.current) return;
    startedRef.current = true;
    const sequence =
      getValues('influencerMonthlyReport')?.documentSequence ?? null;
    if (!sequence) {
      snackbar.showError({ message: t(INFLUENCER_MONTHLY_MISSING_MESSAGE) });
      return;
    }
    setValue('influencerMonthlyReport', {
      ...createInfluencerMonthlyReportState(sequence),
      loading: true,
    });
    getNestedDataFetcher<InfluencerMonthlyDraftSource>(
      getInfluencerMonthlyReportDraftSourceUrl(sequence),
    )
      .then((res) => {
        if (!aliveRef.current) return;
        const report = pickInfluencerMonthlyReport(res.display?.source);
        if (!report) throw new Error('empty source');
        const autoSubject = getInfluencerMonthlySubject(
          report.brandName ?? '',
          t,
        );
        setValue('influencerMonthlyReport', {
          ...createInfluencerMonthlyReportState(sequence),
          report,
          autoSubject,
        });
        setValue('subject', autoSubject, { shouldDirty: false });
      })
      .catch(() => {
        if (!aliveRef.current) return;
        setValue(
          'influencerMonthlyReport',
          createInfluencerMonthlyReportState(sequence),
        );
        snackbar.showError({
          message: t(INFLUENCER_MONTHLY_LOAD_FAILED_MESSAGE),
        });
      });
  }, [ready, getValues, setValue, snackbar, t]);

  const content = useMemo(
    () =>
      buildInfluencerMonthlyReportContentHtml({
        report: state?.report ?? null,
        t,
      }),
    [state?.report, t],
  );

  return (
    <FieldBlock className="influencer-monthly-report">
      {state?.loading && (
        <ApprovalNoticeBox role="status" sx={{ mb: 1 }}>
          {t('데이터를 불러오는 중입니다...')}
        </ApprovalNoticeBox>
      )}
      <InfluencerMonthlyReportSnapshot content={content} />
    </FieldBlock>
  );
};

export default InfluencerMonthlyReportField;
